import { useEffect, useState, type RefObject } from "react";
import { LiquidGlassFilter, supportsLiquidGlass } from "@wa/widgets/chrome/liquid-glass";

/**
 * The one filter in the document. The call window is a singleton portal - there
 * is never a second one to collide with - so a fixed id keeps the stylesheet
 * able to name it, which a generated one could not.
 */
export const CALL_GLASS_FILTER = "crm-call-glass";

/**
 * How long the window has to hold still before the map is redrawn.
 *
 * The window morphs between three widths and into the corner pill, and the map
 * is a data URI the browser has to decode - redrawing it per frame would be a
 * decode per frame for a shape that is about to change again. Nothing is lost by
 * waiting: `feImage` stretches whatever map it has across the current filter
 * region, so the glass keeps refracting throughout the morph and only snaps to
 * the exact geometry once the window has arrived.
 */
const SETTLE_MS = 150;

/** Redrawing for four pixels is redrawing for nothing. */
const STEP = 8;

const quantize = (n: number) => Math.max(STEP, Math.round(n / STEP) * STEP);

/**
 * The refraction half of the call window's glass.
 *
 * The tint, the lighting sweep and the rim are the stylesheet's
 * (`.crm-modal.is-glass`); what has to come from here is the displacement map,
 * because it is drawn to the pane's own size and corner radius and CSS has no
 * way to make one. The recipe is the site chrome's Liquid Glass header, borrowed
 * whole - see `@wa/widgets/chrome/liquid-glass`.
 *
 * ⚠ THE CLASS IS ADDED BY SCRIPT AND THAT IS NOT AN OVERSIGHT. Safari and
 * Firefox parse `backdrop-filter: url(#f)` and then draw nothing at all, so a
 * stylesheet that reached for it unconditionally would take the blur away from
 * the two browsers that cannot have the refraction - trading a working frosted
 * pane for a transparent one. `supportsLiquidGlass()` is the gate, and without
 * it the window keeps exactly the glass it had before.
 */
export function CallGlass({
  target,
  radius,
  active,
}: {
  /** The pane to refract. Its `backdrop-filter` is what the filter is hung on. */
  target: RefObject<HTMLElement | null>;
  /** The pane's current corner radius, so the map's corners match its own. */
  radius: number;
  /** There is a window on screen. Nothing is measured or drawn without one. */
  active: boolean;
}) {
  const [size, setSize] = useState<{ w: number; h: number } | null>(null);
  // Resolved on the client, once: this is a user-agent question, and answering
  // it during the first render would make the first paint disagree with itself.
  const [supported, setSupported] = useState(false);

  useEffect(() => {
    setSupported(supportsLiquidGlass() && !window.matchMedia?.("(prefers-reduced-transparency: reduce)").matches);
  }, []);

  useEffect(() => {
    const el = target.current;
    if (!supported || !active || !el) return;
    el.classList.add("is-refract");

    let timer: number | undefined;
    const commit = () => {
      const rect = el.getBoundingClientRect();
      const next = { w: quantize(rect.width), h: quantize(rect.height) };
      setSize((prev) => (prev && prev.w === next.w && prev.h === next.h ? prev : next));
    };
    // The first measurement is taken straight away. A window that opened without
    // a map and grew one a sixth of a second later would announce the effect
    // rather than simply be made of it.
    commit();
    const ro = new ResizeObserver(() => {
      window.clearTimeout(timer);
      timer = window.setTimeout(commit, SETTLE_MS);
    });
    ro.observe(el);

    return () => {
      window.clearTimeout(timer);
      ro.disconnect();
      // The class goes with the filter. Left behind, it would point
      // `backdrop-filter` at a `url(#...)` that no longer resolves, and a
      // backdrop-filter naming a missing filter draws nothing - the window
      // would lose its blur entirely on the way out.
      el.classList.remove("is-refract");
    };
  }, [supported, active, target]);

  if (!supported || !active || !size) return null;
  return <LiquidGlassFilter id={CALL_GLASS_FILTER} width={size.w} height={size.h} radius={radius} strength={44} />;
}
